/**
 * Journey Creation Form - Single-Screen UX Redesign
 *
 * Three-zone single-screen form replacing the multi-step wizard:
 *   Zone 1: Identity (name + description)
 *   Zone 2: Data Source (segmented toggle: File Upload | Databricks)
 *   Zone 3: Advanced Options (collapsible template selector + intake)
 *
 * Key features:
 * - Smart name auto-generation from filename or Databricks table
 * - Real drag-and-drop with visual feedback
 * - Adaptive modal width (640px file / 960px Databricks)
 * - Phase-based rendering: 'form' -> 'analyzing' with cross-fade
 * - File upload progress stages (same panel as Databricks)
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import '../styles/journey.css';
import { ProblemConfirmationModal } from './contextual-validity/ProblemConfirmationModal';
import { TemplateSelector } from './useCaseTemplates/TemplateSelector';
import { TemplateIntakeForm } from './useCaseTemplates/TemplateIntakeForm';
import { useTemplate } from './useCaseTemplates/useUseCaseTemplates';
import type { UseCaseTemplateSummary, IntakeAnswerValue } from './useCaseTemplates/types';
import { DatabricksTableBrowser, type WarehouseState } from './integrations/DatabricksTableBrowser';
import { useApiClient } from '../hooks/useApiClient';
import { useAuth } from '../hooks/useAuth';
import { DomainSelect } from './triangulation/DomainSelect';

/**
 * Pre-bound source for a dataset RE-ANALYSIS (DC-1677). When present, the form
 * skips the Databricks selection UI, pre-fills the saved table + connection +
 * domain, and auto-runs the same analysis the first analysis runs — so a
 * re-analysis shows the identical staged streaming overlay. The new journey's
 * asset reuses the same table location, so it auto-attaches to the dataset.
 */
export interface ReanalyzePrefill {
  connectionId: string;
  catalog: string;
  schema: string;
  table: string;
  domainId: string | null;
  name: string;
}

interface JourneyCreationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (groupId: string) => void;
  /** When set, hides the data source toggle and defaults to the specified source. */
  forceDataSource?: 'file' | 'databricks';
  /**
   * 'modal' (default) renders the classic overlay dialog. 'page' renders the
   * same form inline for full-page hosts (e.g. /import/databricks) — no
   * overlay, no modal header; the host page supplies the headline. The
   * `journey-creation-modal` class is kept in both variants as the style scope.
   */
  variant?: 'modal' | 'page';
  /**
   * When set, the form runs in re-analysis auto-run mode: it pre-binds this
   * source and immediately runs the Databricks analysis (no selection, no extra
   * click), reusing the full first-analysis overlay. See {@link ReanalyzePrefill}.
   */
  reanalyzePrefill?: ReanalyzePrefill;
}

// All 4 stations are always enabled and auto-launched
const REQUIRED_STATIONS = ['data_quality', 'ml_readiness', 'ontology_inference', 'live_checks'];

interface Asset {
  file: File;
  type: 'dataset';
  name: string;
}

// Types for problem inference (DC-733)
interface InferredValue {
  value: string;
  confidence: number;
  reasoning: string;
}

interface InferredProtectedAttribute {
  column: string;
  attribute_type: string;
  confidence: number;
  reasoning: string;
}

interface FeatureRole {
  column: string;
  role: string;
  confidence: number;
}

interface ProblemInference {
  inferred_problem_type: InferredValue;
  inferred_target_column?: InferredValue;
  inferred_domain?: InferredValue;
  inferred_protected_attributes: InferredProtectedAttribute[];
  feature_roles: FeatureRole[];
  overall_confidence: number;
  requires_user_confirmation: boolean;
}

interface SuggestedDeclaration {
  journey_id: string;
  problem_type: string;
  problem_statement: string;
  target_column?: string;
  domain: string;
  sensitivity_level: string;
  protected_attributes: string[];
}

interface InferenceResponse {
  success: boolean;
  inference: ProblemInference;
  suggested_declaration: SuggestedDeclaration;
  error?: string;
}

type DataSource = 'file' | 'databricks';
type Phase = 'form' | 'analyzing';

interface DatabricksJourneyConnection {
  id: string;
  name: string;
  endpointUrl: string;
  defaultCatalog?: string;
  defaultSchema?: string;
  warehouseId?: string;
}

interface DatabricksSelectedTable {
  catalog: string;
  schema: string;
  table: string;
}

// Allowed dataset file extensions
const ALLOWED_EXTENSIONS = ['csv', 'tsv', 'xlsx', 'xls', 'parquet', 'json'];

// Analysis stages for NDJSON streaming progress
interface AnalysisStage {
  id: string;
  label: string;
  status: 'pending' | 'active' | 'done';
}

const DATABRICKS_STAGES: AnalysisStage[] = [
  { id: 'creating_journey', label: 'Creating Journey', status: 'pending' },
  { id: 'registering_asset', label: 'Registering Data Source', status: 'pending' },
  { id: 'fetching_metadata', label: 'Schema Discovery', status: 'pending' },
  { id: 'column_aggregates', label: 'Column Statistics (SQL Pushdown)', status: 'pending' },
  { id: 'data_quality', label: 'Data Quality Analysis', status: 'pending' },
  { id: 'ml_readiness', label: 'ML Readiness Assessment', status: 'pending' },
  { id: 'ontology_inference', label: 'Ontology Inference', status: 'pending' },
  { id: 'fair_assessment', label: 'FAIR Metadata Assessment', status: 'pending' },
  { id: 'enriching_results', label: 'AI-Powered Recommendations', status: 'pending' },
  { id: 'maturity_scoring', label: 'Maturity Scoring', status: 'pending' },
  { id: 'complete', label: 'Analysis Complete', status: 'pending' },
];

const DEEP_ANALYSIS_STAGES: AnalysisStage[] = [
  { id: 'extracting_sample', label: 'Statistical Sample Extraction', status: 'pending' },
  { id: 'deep_ml_analysis', label: 'Deep ML Analysis', status: 'pending' },
];

const FILE_STAGES: AnalysisStage[] = [
  { id: 'creating_journey', label: 'Creating Journey', status: 'pending' },
  { id: 'uploading_file', label: 'Uploading File', status: 'pending' },
  { id: 'registering_asset', label: 'Registering Data Asset', status: 'pending' },
  { id: 'problem_inference', label: 'Problem Inference', status: 'pending' },
  { id: 'launching_stations', label: 'Launching Analysis Stations', status: 'pending' },
  { id: 'data_quality', label: 'Data Quality Analysis', status: 'pending' },
  { id: 'ml_readiness', label: 'ML Readiness Assessment', status: 'pending' },
  { id: 'ontology_inference', label: 'Ontology Inference', status: 'pending' },
  { id: 'fair_assessment', label: 'FAIR Metadata Assessment', status: 'pending' },
  { id: 'complete', label: 'Analysis Complete', status: 'pending' },
];

/** Algorithm details for each analysis stage */
const STAGE_ALGORITHMS: Record<string, Array<{ name: string; detail: string }>> = {
  creating_journey: [],
  uploading_file: [],
  registering_asset: [],
  problem_inference: [
    { name: 'Column Profiling', detail: 'Data type detection and pattern analysis' },
    { name: 'Target Inference', detail: 'Identify likely target columns for ML' },
  ],
  launching_stations: [],
  fetching_metadata: [
    { name: 'Unity Catalog API', detail: 'Column schemas, data types, descriptions' },
  ],
  column_aggregates: [
    { name: 'SQL COUNT / DISTINCT', detail: 'Non-null counts, unique values per column' },
    { name: 'Statistical Moments', detail: 'MEAN, STDDEV, MIN, MAX via SQL pushdown' },
    { name: 'PERCENTILE_APPROX', detail: 'Q1, Median, Q3 for IQR computation' },
  ],
  extracting_sample: [
    { name: 'Stratified Random Sample', detail: 'ORDER BY RAND() for unbiased statistical sample' },
  ],
  data_quality: [
    { name: 'Shannon Entropy', detail: 'Information content per column (H = -\u03A3 p\u00B7log\u2082p)' },
    { name: "Benford's Law \u03C7\u00B2", detail: 'First-digit distribution vs. expected natural distribution' },
    { name: 'Exact Duplicate Detection', detail: 'Full-row hash comparison via SQL GROUP BY' },
    { name: 'IQR Outlier Detection', detail: 'Values below Q1\u22121.5\u00B7IQR or above Q3+1.5\u00B7IQR' },
    { name: 'Mutual Information', detail: 'Pairwise column redundancy via Shannon MI' },
    { name: 'Variance & CV Analysis', detail: 'Zero-variance detection, coefficient of variation' },
    { name: 'Disguised Missing Values', detail: 'Sentinel patterns: N/A, -999, 0000-00-00, unknown' },
    { name: 'Class Imbalance Detection', detail: 'Low-entropy categorical distribution analysis' },
  ],
  ml_readiness: [
    { name: 'Sample Size Adequacy', detail: 'Factor50 rule and statistical power analysis' },
    { name: 'Bias Detection (TSD)', detail: 'Total Statistical Distance & entropy-based imbalance' },
    { name: 'Feature Quality Assessment', detail: 'Correlation, variance, and engineering potential' },
    { name: 'Privacy Risk Analysis', detail: 'PII detection patterns and k-anonymity estimation' },
  ],
  ontology_inference: [
    { name: 'Column Profiling', detail: 'Data type detection, cardinality, sample analysis' },
    { name: 'ML Confidence Scoring', detail: 'Pattern-based concept matching with confidence' },
    { name: 'Ontology Mapping', detail: 'CURIE generation: CDISC, LOINC, Schema.org' },
  ],
  fair_assessment: [
    { name: 'SHACL Shape Validation', detail: 'W3C constraint checking on metadata graph' },
    { name: 'Metadata Quality Scoring', detail: 'Description, tags, column documentation coverage' },
  ],
  deep_ml_analysis: [
    { name: 'Leave-One-Out (LOO)', detail: 'Individual data point influence measurement' },
    { name: 'Mislabel Detection', detail: "Cohen's Kappa inter-annotator agreement" },
    { name: 'Class Separability', detail: 'Fisher discriminant ratio (R-value) analysis' },
  ],
  enriching_results: [
    { name: 'Bridge AI Engine', detail: 'Cross-station synthesis and remediation guidance' },
    { name: 'Educational Content', detail: 'Plain-language explanations and analogies' },
  ],
  maturity_scoring: [
    { name: 'FAIR Maturity Model', detail: 'Pistoia Alliance maturity indicator aggregation' },
    { name: 'DPROD Alignment', detail: 'W3C Data Product ontology scoring' },
  ],
  complete: [],
};

/**
 * Generate a human-friendly journey name from a filename or Databricks table path.
 * "clinical_trials_q1.csv" -> "Clinical Trials Q1 Analysis"
 * "catalog.schema.patient_vitals" -> "Patient Vitals Analysis"
 */
function generateSmartName(source: string): string {
  // Strip file extension
  let base = source.replace(/\.[^/.]+$/, '');
  // If Databricks-style "catalog.schema.table", take only the table name
  const parts = base.split('.');
  if (parts.length >= 3) {
    base = parts[parts.length - 1];
  }
  // Convert underscores/hyphens to spaces, then title-case
  const words = base
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase split
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  if (words.length === 0) return getFallbackName();
  return words.join(' ') + ' Analysis';
}

function getFallbackName(): string {
  const now = new Date();
  const month = now.toLocaleString('en-US', { month: 'short' });
  const day = now.getDate();
  const year = now.getFullYear();
  return `FAIR Assessment ${month} ${day}, ${year}`;
}

/**
 * Generate a journey name for a multi-table bulk selection.
 * - All tables share catalog AND schema  → "main.silver (12 tables)"
 * - All tables share catalog only        → "main (12 tables across 3 schemas)"
 * - Mixed catalogs                       → "FAIR Assessment of 12 tables"
 */
function generateBulkSmartName(tables: DatabricksSelectedTable[]): string {
  const n = tables.length;
  if (n === 0) return getFallbackName();
  const catalogs = new Set(tables.map(t => t.catalog));
  if (catalogs.size === 1) {
    const catalog = tables[0].catalog;
    const schemas = new Set(tables.map(t => t.schema));
    if (schemas.size === 1) {
      return `${catalog}.${tables[0].schema} (${n} tables)`;
    }
    return `${catalog} (${n} tables across ${schemas.size} schemas)`;
  }
  return `FAIR Assessment of ${n} tables`;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isValidDatasetFile(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ALLOWED_EXTENSIONS.includes(ext || '');
}

export const JourneyCreationForm: React.FC<JourneyCreationFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  forceDataSource,
  variant = 'modal',
  reanalyzePrefill,
}) => {
  const navigate = useNavigate();
  const { authFetch, userId } = useApiClient();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Core form state ---
  const [phase, setPhase] = useState<Phase>('form');
  const [dataSource, setDataSource] = useState<DataSource>('file');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nameManuallyEdited, setNameManuallyEdited] = useState(false);
  // DC-1493 — optional triangulation domain assignment at journey creation time.
  const [domainId, setDomainId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // File state
  const [assets, setAssets] = useState<Asset[]>([]);
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);

  // Databricks state
  const [databricksConnections, setDatabricksConnections] = useState<DatabricksJourneyConnection[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<DatabricksSelectedTable | null>(null);
  const [selectedTables, setSelectedTables] = useState<DatabricksSelectedTable[]>([]);
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [deepAnalysisEnabled, setDeepAnalysisEnabled] = useState(false);
  const [warehouseState, setWarehouseState] = useState<WarehouseState>('UNKNOWN');
  // Bulk-mode progress label: "Table X of N: catalog.schema.table" — populated from
  // tableIndex/tableTotal/tablePath fields the server emits in multi-table mode.
  const [bulkTableLabel, setBulkTableLabel] = useState<string | null>(null);

  // Template state
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedTemplateSummary, setSelectedTemplateSummary] = useState<UseCaseTemplateSummary | null>(null);
  const [intakeAnswers, setIntakeAnswers] = useState<Record<string, IntakeAnswerValue>>({});
  const { template: selectedTemplate, loading: templateLoading } = useTemplate(selectedTemplateId);

  // Analysis progress state
  const [analysisStages, setAnalysisStages] = useState<AnalysisStage[]>([]);
  const [analysisElapsed, setAnalysisElapsed] = useState(0);
  const [analysisLabel, setAnalysisLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Problem inference modal state
  const [showProblemModal, setShowProblemModal] = useState(false);
  const [inferenceData, setInferenceData] = useState<{
    inference: ProblemInference;
    suggestedDeclaration: SuggestedDeclaration;
    journeyId: string;
    assetIds: string[];
  } | null>(null);

  // Fetch Databricks connections on mount
  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const resp = await authFetch('/api/databricks-journey/journey-connections');
        if (resp.ok) {
          const data = await resp.json();
          const conns = data.connections || [];
          setDatabricksConnections(conns);
          if (conns.length === 1) {
            setSelectedConnection(conns[0].id);
          }
        }
      } catch {
        // Non-blocking
      }
    };
    if (isOpen) fetchConnections();
  }, [isOpen, authFetch]);

  // Smart name auto-generation
  useEffect(() => {
    if (nameManuallyEdited) return;
    if (dataSource === 'file' && assets.length > 0) {
      setName(generateSmartName(assets[0].name));
    } else if (dataSource === 'databricks' && bulkSelectMode && selectedTables.length > 1) {
      setName(generateBulkSmartName(selectedTables));
    } else if (dataSource === 'databricks' && bulkSelectMode && selectedTables.length === 1) {
      const t = selectedTables[0];
      setName(generateSmartName(`${t.catalog}.${t.schema}.${t.table}`));
    } else if (dataSource === 'databricks' && !bulkSelectMode && selectedTable) {
      setName(generateSmartName(`${selectedTable.catalog}.${selectedTable.schema}.${selectedTable.table}`));
    } else {
      setName('');
    }
  }, [assets, selectedTable, selectedTables, bulkSelectMode, dataSource, nameManuallyEdited]);

  // Reset form when modal closes/opens
  useEffect(() => {
    if (isOpen) {
      setPhase('form');
      setDataSource(forceDataSource || 'file');
      setName('');
      setDescription('');
      setNameManuallyEdited(false);
      setIsDragOver(false);
      setShowAdvanced(false);
      setAssets([]);
      setUploadWarning(null);
      setSelectedTable(null);
      setSelectedTables([]);
      setBulkSelectMode(false);
      setBulkTableLabel(null);
      setDeepAnalysisEnabled(false);
      setSelectedTemplateId(null);
      setSelectedTemplateSummary(null);
      setIntakeAnswers({});
      setAnalysisStages([]);
      setAnalysisElapsed(0);
      setAnalysisLabel('');
      setError(null);
      setShowProblemModal(false);
      setInferenceData(null);
    }
  }, [isOpen, forceDataSource]);

  // --- Re-analysis auto-run (DC-1677) ---------------------------------------
  // Given a reanalyzePrefill, pre-bind the saved Databricks source (no selection
  // UI), then auto-run the SAME analysis the first analysis runs — so a
  // re-analysis shows the identical staged streaming overlay. The new journey's
  // asset reuses the same table location, so it auto-attaches to the dataset.
  // (Placed with the other hooks, before any early return, per rules-of-hooks.)
  const reanalyzeStartedRef = useRef(false);

  useEffect(() => {
    if (!reanalyzePrefill) return;
    setDataSource('databricks');
    setSelectedConnection(reanalyzePrefill.connectionId);
    setSelectedTable({
      catalog: reanalyzePrefill.catalog,
      schema: reanalyzePrefill.schema,
      table: reanalyzePrefill.table,
    });
    setDomainId(reanalyzePrefill.domainId);
    setName(reanalyzePrefill.name);
    // Keep our "— re-analysis" name; the smart-name effect skips when manual.
    setNameManuallyEdited(true);
  }, [reanalyzePrefill]);

  useEffect(() => {
    if (!reanalyzePrefill || reanalyzeStartedRef.current) return;
    // Wait until every pre-filled value has committed before auto-running —
    // handleDatabricksSubmit reads this state and React updates are async.
    const bound =
      phase === 'form' &&
      selectedConnection === reanalyzePrefill.connectionId &&
      selectedTable?.catalog === reanalyzePrefill.catalog &&
      selectedTable?.schema === reanalyzePrefill.schema &&
      selectedTable?.table === reanalyzePrefill.table &&
      domainId === reanalyzePrefill.domainId;
    if (bound) {
      reanalyzeStartedRef.current = true;
      void handleDatabricksSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- guarded one-shot: fire once the prefilled source is bound; handleDatabricksSubmit reads fresh state at call time
  }, [reanalyzePrefill, phase, selectedConnection, selectedTable, domainId]);

  // --- Drag and drop ---
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set false if we're leaving the dropzone entirely
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const { clientX, clientY } = e;
    if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  }, []);

  // Early return after ALL hooks
  if (!isOpen) return null;

  // --- Handlers (not hooks, safe after early return) ---
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (phase === 'analyzing') return; // Don't close during analysis
      onClose();
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setNameManuallyEdited(true);
  };

  const handleTemplateSelect = (templateId: string | null, template: UseCaseTemplateSummary | null) => {
    setSelectedTemplateId(templateId);
    setSelectedTemplateSummary(template);
    setIntakeAnswers({});
  };

  const handleIntakeSubmit = (answers: Record<string, IntakeAnswerValue>) => {
    setIntakeAnswers(answers);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
    // Reset input so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const processFiles = (files: File[]) => {
    setUploadWarning(null);
    const validFiles: Asset[] = [];
    const rejectedFiles: string[] = [];

    for (const file of files) {
      if (isValidDatasetFile(file)) {
        validFiles.push({ file, type: 'dataset', name: file.name });
      } else {
        rejectedFiles.push(file.name);
      }
    }

    if (rejectedFiles.length > 0) {
      setUploadWarning(
        `${rejectedFiles.length} file(s) skipped: ${rejectedFiles.join(', ')}. ` +
        'Only dataset files (CSV, Excel, Parquet, JSON) are accepted.'
      );
    }
    if (validFiles.length > 0) {
      setAssets(prev => [...prev, ...validFiles]);
    }
  };

  const removeAsset = (index: number) => {
    setAssets(prev => prev.filter((_, i) => i !== index));
  };

  // --- Analysis progress helpers ---
  const markStageActive = (stageId: string) => {
    setAnalysisStages(prev =>
      prev.map((s, i) => {
        const targetIdx = prev.findIndex(x => x.id === stageId);
        if (i < targetIdx) return { ...s, status: 'done' as const };
        if (i === targetIdx) return { ...s, status: 'active' as const };
        return s;
      })
    );
  };

  const markStageDone = (stageId: string) => {
    setAnalysisStages(prev =>
      prev.map(s => (s.id === stageId ? { ...s, status: 'done' as const } : s))
    );
  };

  const markAllDone = () => {
    setAnalysisStages(prev => prev.map(s => ({ ...s, status: 'done' as const })));
  };

  // --- Launch stations (file path) ---
  const launchStations = async (journeyId: string, assetIds: string[]) => {
    if (assetIds.length === 0) return;
    const stationNames: Record<string, string> = {
      data_quality: 'Data Quality',
      ml_readiness: 'ML Readiness',
      ontology_inference: 'Ontology Inference',
      live_checks: 'FAIR Assessment',
    };

    for (const stationId of REQUIRED_STATIONS) {
      setAnalysisLabel(`Starting ${stationNames[stationId]} analysis...`);
      try {
        await authFetch(`/api/journey/${journeyId}/station/${stationId}/run`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ asset_ids: assetIds, timeout_ms: 300000 }),
        });
      } catch (stationErr) {
        console.warn(`Failed to start ${stationId}:`, stationErr);
      }
    }
  };

  // --- Unified submit ---
  const handleSubmit = async () => {
    // DC-1619 — a triangulation domain is required at creation (Exploratory is
    // the catch-all). Guard here covers both the file and Databricks paths.
    if (!domainId) {
      setError('Please select a domain before creating this journey.');
      return;
    }
    if (dataSource === 'file') {
      await handleFileSubmit();
    } else {
      await handleDatabricksSubmit();
    }
  };

  // --- File upload submit ---
  const handleFileSubmit = async () => {
    setPhase('analyzing');
    setError(null);
    setAnalysisStages(FILE_STAGES.map(s => ({ ...s, status: 'pending' as const })));
    setAnalysisElapsed(0);

    const startTime = Date.now();
    const elapsedTimer = setInterval(() => setAnalysisElapsed(Date.now() - startTime), 500);

    try {
      // 1. Create journey
      markStageActive('creating_journey');
      setAnalysisLabel('Creating journey...');

      const journeyMetadata: Record<string, unknown> = {
        enabled_stations: selectedTemplate
          ? [...selectedTemplate.required_stations, ...selectedTemplate.optional_stations]
          : REQUIRED_STATIONS,
        auto_launched: true,
      };

      if (selectedTemplateId && selectedTemplate) {
        journeyMetadata.template_id = selectedTemplateId;
        journeyMetadata.template_name = selectedTemplate.name;
        journeyMetadata.template_category = selectedTemplate.category;
        journeyMetadata.intake_answers = intakeAnswers;
      }

      const effectiveName = name.trim() || getFallbackName();

      const createResponse = await authFetch('/api/journey/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: effectiveName,
          description: description || '',
          captain: { user_id: userId || '', name: user?.name || '', email: user?.email || '' },
          crew: [],
          metadata: journeyMetadata,
          domain_id: domainId,
        }),
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.error || `Failed to create journey: ${createResponse.statusText}`);
      }

      const { journey } = await createResponse.json();
      markStageDone('creating_journey');

      // 2. Upload files
      markStageActive('uploading_file');
      for (let i = 0; i < assets.length; i++) {
        const asset = assets[i];
        setAnalysisLabel(`Uploading ${asset.name} (${i + 1}/${assets.length})...`);

        const uploadFormData = new FormData();
        uploadFormData.append('file', asset.file);
        uploadFormData.append('journey_id', journey.group_id);

        const uploadResponse = await authFetch('/api/journey/upload', {
          method: 'POST',
          body: uploadFormData,
        });

        if (!uploadResponse.ok) {
          const errorData = await uploadResponse.json().catch(() => ({}));
          const errorMsg = (errorData as Record<string, string>).error || `Upload failed for ${asset.name}`;
          if (uploadResponse.status === 413 || errorMsg.includes('too large') || errorMsg.includes('LIMIT_FILE_SIZE')) {
            throw new Error(`File "${asset.name}" is too large. Maximum size is 500 MB.`);
          }
          throw new Error(`Failed to upload "${asset.name}": ${errorMsg}`);
        }

        const uploadData = await uploadResponse.json();
        const fileLocation = uploadData.location || `/uploads/${journey.group_id}/${asset.name}`;

        const assetData = {
          asset_id: uuidv4(),
          type: asset.type,
          name: asset.name,
          location: fileLocation,
          description: `${asset.type} file: ${asset.name}`,
          metadata: { file_size: asset.file.size, file_type: asset.file.type },
        };

        await authFetch(`/api/journey/${journey.group_id}/assets/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(assetData),
        });
      }
      markStageDone('uploading_file');

      // 3. Register asset
      markStageActive('registering_asset');
      setAnalysisLabel('Registering data assets...');
      const journeyResponse = await authFetch(`/api/journey/${journey.group_id}`);
      const updatedJourney = await journeyResponse.json();
      const assetIds = updatedJourney.assets?.map((a: { asset_id: string }) => a.asset_id) || [];
      markStageDone('registering_asset');

      // 4. Problem inference
      markStageActive('problem_inference');
      setAnalysisLabel('Analyzing dataset for problem inference...');
      const firstAsset = assets[0];
      const supportedExt = ['csv', 'tsv', 'xlsx', 'xls'];
      const ext = firstAsset?.name.split('.').pop()?.toLowerCase() || '';

      if (assetIds.length > 0 && supportedExt.includes(ext)) {
        try {
          const filePath = `/uploads/${journey.group_id}/${firstAsset.name}`;
          const inferResponse = await authFetch(
            `/api/contextual-validity/journeys/${journey.group_id}/infer-from-file`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ file_path: filePath }),
            }
          );

          if (inferResponse.ok) {
            const inferData: InferenceResponse = await inferResponse.json();
            if (inferData.success && inferData.inference) {
              markStageDone('problem_inference');
              clearInterval(elapsedTimer);
              setInferenceData({
                inference: inferData.inference,
                suggestedDeclaration: inferData.suggested_declaration,
                journeyId: journey.group_id,
                assetIds,
              });
              setShowProblemModal(true);
              return;
            }
          }
        } catch (inferErr) {
          console.warn('[JourneyCreationForm] Problem inference error:', inferErr);
        }
      }
      markStageDone('problem_inference');

      // 5. Launch stations
      markStageActive('launching_stations');
      setAnalysisLabel('Launching analysis stations...');
      await launchStations(journey.group_id, assetIds);
      markStageDone('launching_stations');

      // Mark analysis stations as done
      markStageActive('data_quality');
      markStageDone('data_quality');
      markStageActive('ml_readiness');
      markStageDone('ml_readiness');
      markStageActive('ontology_inference');
      markStageDone('ontology_inference');
      markStageActive('fair_assessment');
      markStageDone('fair_assessment');

      // Complete
      markAllDone();
      setAnalysisLabel('Analysis complete! Redirecting...');
      await new Promise(resolve => setTimeout(resolve, 800));

      if (onSuccess) {
        onSuccess(journey.group_id);
      } else {
        navigate(`/journey/${journey.group_id}`);
      }
      onClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create journey';
      setError(errorMessage);
      setPhase('form');
    } finally {
      clearInterval(elapsedTimer);
    }
  };

  // --- Databricks submit ---
  const handleDatabricksSubmit = async () => {
    if (!selectedConnection) return;
    // In bulk mode, defer to the bulk submit path; otherwise use the legacy
    // single-table path. The two share enough that they could be unified, but
    // keeping single-table behavior byte-for-byte identical here protects
    // the existing flow from regressions while bulk is exercised by the new
    // tests.
    if (bulkSelectMode) {
      if (selectedTables.length === 0) return;
      await handleDatabricksBulkSubmit();
      return;
    }
    if (!selectedTable) return;

    setPhase('analyzing');
    setError(null);

    const stages = deepAnalysisEnabled
      ? [
          ...DATABRICKS_STAGES.slice(0, 8),
          ...DEEP_ANALYSIS_STAGES,
          ...DATABRICKS_STAGES.slice(8),
        ]
      : [...DATABRICKS_STAGES];
    setAnalysisStages(stages.map(s => ({ ...s, status: 'pending' as const })));
    setAnalysisElapsed(0);

    const startTime = Date.now();
    const elapsedTimer = setInterval(() => setAnalysisElapsed(Date.now() - startTime), 500);

    try {
      // 1. Create journey
      markStageActive('creating_journey');
      setAnalysisLabel('Creating journey...');

      const journeyMetadata: Record<string, unknown> = {
        enabled_stations: REQUIRED_STATIONS,
        auto_launched: true,
        data_source: 'databricks',
        databricks_connection_id: selectedConnection,
        databricks_table: `${selectedTable.catalog}.${selectedTable.schema}.${selectedTable.table}`,
        deep_analysis_enabled: deepAnalysisEnabled,
      };

      if (selectedTemplateId && selectedTemplate) {
        journeyMetadata.template_id = selectedTemplateId;
        journeyMetadata.template_name = selectedTemplate.name;
        journeyMetadata.template_category = selectedTemplate.category;
        journeyMetadata.intake_answers = intakeAnswers;
      }

      const effectiveName = name.trim() || getFallbackName();

      const createResponse = await authFetch('/api/journey/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: effectiveName,
          description: description || '',
          captain: { user_id: userId || '', name: user?.name || '', email: user?.email || '' },
          crew: [],
          metadata: journeyMetadata,
          domain_id: domainId,
        }),
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.error || `Failed to create journey: ${createResponse.statusText}`);
      }

      const { journey } = await createResponse.json();
      markStageDone('creating_journey');

      // 2. Register virtual asset
      markStageActive('registering_asset');
      setAnalysisLabel('Registering data source...');
      const assetData = {
        asset_id: uuidv4(),
        type: 'dataset',
        name: `${selectedTable.catalog}.${selectedTable.schema}.${selectedTable.table}`,
        location: `databricks://${selectedTable.catalog}.${selectedTable.schema}.${selectedTable.table}`,
        description: `Databricks Unity Catalog table: ${selectedTable.catalog}.${selectedTable.schema}.${selectedTable.table}`,
        metadata: {
          source: 'databricks',
          connectionId: selectedConnection,
          catalog: selectedTable.catalog,
          schema: selectedTable.schema,
          table: selectedTable.table,
        },
      };

      await authFetch(`/api/journey/${journey.group_id}/assets/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assetData),
      });
      markStageDone('registering_asset');

      // 3. Run SQL pushdown analysis with NDJSON streaming
      setAnalysisLabel('Starting analysis...');
      const analysisResponse = await authFetch('/api/databricks-journey/analyze-for-journey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connectionId: selectedConnection,
          catalog: selectedTable.catalog,
          schema: selectedTable.schema,
          table: selectedTable.table,
          journeyId: journey.group_id,
          analysisTypes: ['data_quality', 'ml_readiness', 'ontology_inference', 'fair_assessment'],
          options: { sampleSize: 1000 },
          deepAnalysis: deepAnalysisEnabled,
        }),
      });

      if (!analysisResponse.ok) {
        const errData = await analysisResponse.json().catch(() => ({ error: {} }));
        const msg = (errData as Record<string, Record<string, string>>).error?.message || `Analysis failed (HTTP ${analysisResponse.status})`;
        throw new Error(msg);
      }

      // Read NDJSON stream
      const reader = analysisResponse.body?.getReader();
      if (!reader) throw new Error('Streaming not supported');

      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;
      let streamError: string | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);
            if (event.event === 'progress') {
              setAnalysisStages(prev => {
                const stageIndex = prev.findIndex(s => s.id === event.stage);
                return prev.map((s, i) => {
                  if (i < stageIndex) return { ...s, status: 'done' as const };
                  if (i === stageIndex) return { ...s, status: 'active' as const };
                  return s;
                });
              });
              setAnalysisLabel(event.message);
            } else if (event.event === 'complete') {
              completed = true;
            } else if (event.event === 'error') {
              streamError = event.message;
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }

      if (streamError) throw new Error(streamError);

      if (completed) {
        markAllDone();
        setAnalysisLabel('Analysis complete! Redirecting...');
        await new Promise(resolve => setTimeout(resolve, 800));

        if (onSuccess) {
          onSuccess(journey.group_id);
        } else {
          navigate(`/journey/${journey.group_id}`);
        }
        onClose();
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze Databricks table';
      setError(errorMessage);
      setPhase('form');
    } finally {
      clearInterval(elapsedTimer);
    }
  };

  // --- Databricks bulk submit (multi-table single-journey) ---
  const handleDatabricksBulkSubmit = async () => {
    if (!selectedConnection || selectedTables.length === 0) return;

    setPhase('analyzing');
    setError(null);
    setBulkTableLabel(null);

    // Bulk-mode timeline reuses the same stages as single-table; the stages
    // represent the CURRENT table's progress and reset to pending whenever
    // the server reports a new tableIndex. The top progress bar (driven by
    // the server's scaled `progress` field) shows cumulative progress.
    const baseStages = deepAnalysisEnabled
      ? [
          ...DATABRICKS_STAGES.slice(0, 8),
          ...DEEP_ANALYSIS_STAGES,
          ...DATABRICKS_STAGES.slice(8),
        ]
      : [...DATABRICKS_STAGES];
    setAnalysisStages(baseStages.map(s => ({ ...s, status: 'pending' as const })));
    setAnalysisElapsed(0);

    const startTime = Date.now();
    const elapsedTimer = setInterval(() => setAnalysisElapsed(Date.now() - startTime), 500);

    // Track per-table failures for the final error summary.
    const tableFailures: Array<{ tablePath: string; error: string }> = [];

    try {
      // 1. Create journey (single journey for the whole batch)
      markStageActive('creating_journey');
      setAnalysisLabel('Creating journey...');

      const tablePaths = selectedTables.map(
        t => `${t.catalog}.${t.schema}.${t.table}`,
      );

      const journeyMetadata: Record<string, unknown> = {
        enabled_stations: REQUIRED_STATIONS,
        auto_launched: true,
        data_source: 'databricks',
        databricks_connection_id: selectedConnection,
        databricks_tables: tablePaths,
        databricks_table_count: selectedTables.length,
        deep_analysis_enabled: deepAnalysisEnabled,
        bulk_mode: true,
      };

      if (selectedTemplateId && selectedTemplate) {
        journeyMetadata.template_id = selectedTemplateId;
        journeyMetadata.template_name = selectedTemplate.name;
        journeyMetadata.template_category = selectedTemplate.category;
        journeyMetadata.intake_answers = intakeAnswers;
      }

      const effectiveName = name.trim() || getFallbackName();

      const createResponse = await authFetch('/api/journey/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: effectiveName,
          description: description || '',
          captain: { user_id: userId || '', name: user?.name || '', email: user?.email || '' },
          crew: [],
          metadata: journeyMetadata,
          domain_id: domainId,
        }),
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json();
        throw new Error(errorData.error || `Failed to create journey: ${createResponse.statusText}`);
      }

      const { journey } = await createResponse.json();
      markStageDone('creating_journey');

      // 2. Register one virtual asset per table (sequential)
      markStageActive('registering_asset');
      for (let i = 0; i < selectedTables.length; i++) {
        const t = selectedTables[i];
        setAnalysisLabel(
          `Registering data source ${i + 1}/${selectedTables.length}: ${t.catalog}.${t.schema}.${t.table}`,
        );
        const assetData = {
          asset_id: uuidv4(),
          type: 'dataset',
          name: `${t.catalog}.${t.schema}.${t.table}`,
          location: `databricks://${t.catalog}.${t.schema}.${t.table}`,
          description: `Databricks Unity Catalog table: ${t.catalog}.${t.schema}.${t.table}`,
          metadata: {
            source: 'databricks',
            connectionId: selectedConnection,
            catalog: t.catalog,
            schema: t.schema,
            table: t.table,
          },
        };
        await authFetch(`/api/journey/${journey.group_id}/assets/add`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(assetData),
        });
      }
      markStageDone('registering_asset');

      // 3. Run multi-table analysis. The server fans out serially and tags
      // every event with tableIndex / tableTotal / tablePath so we can show
      // per-table progress.
      setAnalysisLabel('Starting analysis...');
      const analysisResponse = await authFetch('/api/databricks-journey/analyze-for-journey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connectionId: selectedConnection,
          tables: selectedTables,
          journeyId: journey.group_id,
          analysisTypes: ['data_quality', 'ml_readiness', 'ontology_inference', 'fair_assessment'],
          options: { sampleSize: 1000 },
          deepAnalysis: deepAnalysisEnabled,
        }),
      });

      if (!analysisResponse.ok) {
        const errData = await analysisResponse.json().catch(() => ({ error: {} }));
        const msg =
          (errData as Record<string, Record<string, string>>).error?.message ||
          `Analysis failed (HTTP ${analysisResponse.status})`;
        throw new Error(msg);
      }

      const reader = analysisResponse.body?.getReader();
      if (!reader) throw new Error('Streaming not supported');

      const decoder = new TextDecoder();
      let buffer = '';
      let completed = false;
      let streamError: string | null = null;
      let lastTableIndex = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);
            if (event.event === 'progress') {
              const tableIndex: number | undefined = event.tableIndex;
              const tableTotal: number | undefined = event.tableTotal;
              const tablePath: string | undefined = event.tablePath;

              if (tableIndex && tableTotal && tablePath) {
                if (tableIndex !== lastTableIndex) {
                  // New table starting — reset post-asset stages to pending
                  // so the timeline reads as the CURRENT table's progress.
                  setAnalysisStages(prev =>
                    prev.map(s =>
                      s.id === 'creating_journey' || s.id === 'registering_asset'
                        ? { ...s, status: 'done' as const }
                        : { ...s, status: 'pending' as const },
                    ),
                  );
                  lastTableIndex = tableIndex;
                }
                setBulkTableLabel(`Analyzing table ${tableIndex} of ${tableTotal}: ${tablePath}`);
              }

              setAnalysisStages(prev => {
                const stageIndex = prev.findIndex(s => s.id === event.stage);
                if (stageIndex < 0) return prev;
                return prev.map((s, i) => {
                  if (i < stageIndex) return { ...s, status: 'done' as const };
                  if (i === stageIndex) return { ...s, status: 'active' as const };
                  return s;
                });
              });
              setAnalysisLabel(event.message);
            } else if (event.event === 'table_failed') {
              tableFailures.push({
                tablePath: event.tablePath || 'unknown',
                error: event.error || 'Unknown error',
              });
            } else if (event.event === 'complete') {
              completed = true;
            } else if (event.event === 'error') {
              streamError = event.message;
            }
          } catch {
            // Skip malformed JSON
          }
        }
      }

      if (streamError) throw new Error(streamError);

      if (completed) {
        markAllDone();
        setBulkTableLabel(null);
        const successCount = selectedTables.length - tableFailures.length;
        if (tableFailures.length === 0) {
          setAnalysisLabel(`All ${selectedTables.length} tables analyzed. Redirecting...`);
        } else {
          setAnalysisLabel(
            `${successCount} of ${selectedTables.length} tables analyzed; ${tableFailures.length} failed. Redirecting...`,
          );
        }
        await new Promise(resolve => setTimeout(resolve, 1200));

        if (onSuccess) {
          onSuccess(journey.group_id);
        } else {
          navigate(`/journey/${journey.group_id}`);
        }
        onClose();
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze Databricks tables';
      const detail =
        tableFailures.length > 0
          ? `${errorMessage}\nPer-table failures:\n${tableFailures
              .map(f => `  • ${f.tablePath}: ${f.error}`)
              .join('\n')}`
          : errorMessage;
      setError(detail);
      setPhase('form');
    } finally {
      clearInterval(elapsedTimer);
    }
  };

  // --- Problem modal handlers ---
  const handleProblemConfirm = async (_declaration: SuggestedDeclaration) => {
    if (!inferenceData) return;
    setShowProblemModal(false);

    // Re-enter analyzing phase for station launch
    setAnalysisStages(prev => [...prev]);
    markStageActive('launching_stations');
    setAnalysisLabel('Launching analysis stations...');

    const startTime = Date.now();
    const elapsedTimer = setInterval(() => setAnalysisElapsed(Date.now() - startTime), 500);

    try {
      await launchStations(inferenceData.journeyId, inferenceData.assetIds);
      markStageDone('launching_stations');
      markAllDone();
      setAnalysisLabel('Analysis complete! Redirecting...');
      await new Promise(resolve => setTimeout(resolve, 800));

      if (onSuccess) {
        onSuccess(inferenceData.journeyId);
      } else {
        navigate(`/journey/${inferenceData.journeyId}`);
      }
      onClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to launch stations';
      setError(errorMessage);
      setPhase('form');
    } finally {
      clearInterval(elapsedTimer);
      setInferenceData(null);
    }
  };

  const handleProblemModalClose = async () => {
    if (!inferenceData) {
      setShowProblemModal(false);
      return;
    }
    setShowProblemModal(false);

    markStageActive('launching_stations');
    setAnalysisLabel('Launching analysis stations...');

    const startTime = Date.now();
    const elapsedTimer = setInterval(() => setAnalysisElapsed(Date.now() - startTime), 500);

    try {
      await launchStations(inferenceData.journeyId, inferenceData.assetIds);
      markStageDone('launching_stations');
      markAllDone();
      setAnalysisLabel('Analysis complete! Redirecting...');
      await new Promise(resolve => setTimeout(resolve, 800));

      if (onSuccess) {
        onSuccess(inferenceData.journeyId);
      } else {
        navigate(`/journey/${inferenceData.journeyId}`);
      }
      onClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to launch stations';
      setError(errorMessage);
      setPhase('form');
    } finally {
      clearInterval(elapsedTimer);
      setInferenceData(null);
    }
  };

  // --- Validation ---
  const canSubmit = (): boolean => {
    const hasName =
      name.trim().length > 0 || assets.length > 0 || selectedTable !== null || selectedTables.length > 0;
    if (dataSource === 'file') {
      return assets.length > 0 && hasName;
    }
    // Allow submit if warehouse is RUNNING or UNKNOWN (browsing success proves connectivity)
    // Only block on definitively non-functional states
    const warehouseOk = warehouseState !== 'STOPPED' && warehouseState !== 'STOPPING';
    if (bulkSelectMode) {
      // Server enforces 1-50 hard cap; mirror it here.
      const inRange = selectedTables.length >= 1 && selectedTables.length <= 50;
      return inRange && selectedConnection !== null && warehouseOk && hasName;
    }
    return selectedTable !== null && selectedConnection !== null && warehouseOk && hasName;
  };

  // --- Render ---
  const isAnalyzing = phase === 'analyzing';
  const modalWidthClass = dataSource === 'databricks' ? 'jcf-modal--wide' : 'jcf-modal--normal';

  const doneCount = analysisStages.filter(s => s.status === 'done').length;
  const pct = analysisStages.length > 0 ? Math.round((doneCount / analysisStages.length) * 100) : 0;

  const isPage = variant === 'page';

  const formCard = (
      <div className={`journey-creation-modal ${isPage ? 'jcf-page' : modalWidthClass}`}>
        {/* Header — modal variant only; page hosts supply their own headline */}
        {!isPage && (
          <div className="modal-header">
            <h2>Create New Journey</h2>
            <button
              className="modal-close"
              onClick={onClose}
              aria-label="Close"
              disabled={isAnalyzing}
            >
              &times;
            </button>
          </div>
        )}

        <div className="modal-content">
          {/* ===== FORM PHASE ===== */}
          <div className={`jcf-form-phase ${phase === 'form' ? 'jcf-phase--visible' : 'jcf-phase--hidden'}`}>
            {/* ZONE 1: Identity */}
            <div className="jcf-zone">
              <div className="form-group">
                <label htmlFor="jcf-name">Journey Name</label>
                <input
                  id="jcf-name"
                  type="text"
                  className={`form-input ${!nameManuallyEdited && name ? 'jcf-auto-name' : ''}`}
                  placeholder={getFallbackName()}
                  value={name}
                  onChange={handleNameChange}
                  autoFocus
                />
                {!nameManuallyEdited && name && (
                  <span className="form-hint">Auto-generated from your data source. Click to edit.</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="jcf-desc">Description <span className="jcf-optional">(optional)</span></label>
                <textarea
                  id="jcf-desc"
                  className="form-textarea"
                  placeholder="What are you assessing?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                />
              </div>

              {/* DC-1619 — required domain assignment; surfaces this journey in the domain's TriangulationView. */}
              <div className="form-group">
                <DomainSelect
                  required
                  value={domainId}
                  onChange={setDomainId}
                  helpText="Required. The domain this journey belongs to — it feeds that domain's Triangulation View. Choose Exploratory for test or ad-hoc datasets."
                />
              </div>
            </div>

            {/* ZONE 2: Data Source */}
            <div className="jcf-zone">
              {/* Segmented toggle — hidden when forceDataSource is set */}
              {!forceDataSource && (
                <div className="jcf-toggle">
                  <button
                    type="button"
                    className={`jcf-toggle-btn ${dataSource === 'file' ? 'jcf-toggle-btn--active' : ''}`}
                    onClick={() => {
                      setDataSource('file');
                      setSelectedTable(null);
                    }}
                  >
                    <span className="jcf-toggle-icon">&#128196;</span>
                    File Upload
                  </button>
                  <button
                    type="button"
                    className={`jcf-toggle-btn ${dataSource === 'databricks' ? 'jcf-toggle-btn--active' : ''} ${databricksConnections.length === 0 ? 'jcf-toggle-btn--disabled' : ''}`}
                    onClick={() => {
                      if (databricksConnections.length > 0) {
                        setDataSource('databricks');
                        setAssets([]);
                        setUploadWarning(null);
                      }
                    }}
                    disabled={databricksConnections.length === 0}
                    title={databricksConnections.length === 0 ? 'Configure a connection in Settings > Integrations first' : ''}
                  >
                    <span className="jcf-toggle-icon">&#9889;</span>
                    Databricks
                    {databricksConnections.length === 0 && (
                      <span className="jcf-toggle-badge">Not configured</span>
                    )}
                  </button>
                </div>
              )}

              {/* File upload mode */}
              {dataSource === 'file' && (
                <div className="jcf-file-section">
                  {/* Dropzone */}
                  <div
                    className={`jcf-dropzone ${isDragOver ? 'jcf-dropzone--active' : ''} ${assets.length > 0 ? 'jcf-dropzone--compact' : ''}`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".csv,.tsv,.xlsx,.xls,.parquet,.json"
                      onChange={handleFileInputChange}
                      className="jcf-file-input"
                    />
                    {assets.length === 0 ? (
                      <>
                        <div className="jcf-dropzone-icon">
                          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                            <rect x="4" y="8" width="40" height="32" rx="4" stroke="var(--tri-slate)" strokeWidth="2" strokeDasharray="4 3" />
                            <path d="M24 18v12M18 24h12" stroke="var(--tri-teal)" strokeWidth="2" strokeLinecap="round" />
                          </svg>
                        </div>
                        <div className="jcf-dropzone-text">
                          <strong>Drop files here</strong> or click to browse
                        </div>
                        <div className="jcf-dropzone-hint">
                          CSV, Excel, Parquet, or JSON
                        </div>
                      </>
                    ) : (
                      <div className="jcf-dropzone-add">+ Add more files</div>
                    )}
                  </div>

                  {/* Warning */}
                  {uploadWarning && (
                    <div className="upload-warning">
                      <span className="warning-icon">&#9888;</span>
                      {uploadWarning}
                    </div>
                  )}

                  {/* File chips */}
                  {assets.length > 0 && (
                    <div className="jcf-file-chips">
                      {assets.map((asset, idx) => (
                        <div key={idx} className="jcf-file-chip">
                          <span className="jcf-chip-icon">&#128202;</span>
                          <span className="jcf-chip-name">{asset.name}</span>
                          <span className="jcf-chip-size">{formatFileSize(asset.file.size)}</span>
                          <button
                            className="jcf-chip-remove"
                            onClick={(e) => { e.stopPropagation(); removeAsset(idx); }}
                            aria-label={`Remove ${asset.name}`}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Databricks mode */}
              {dataSource === 'databricks' && (
                <div className="jcf-databricks-section">
                  {/* Empty state — no connection configured */}
                  {databricksConnections.length === 0 && (
                    <div className="jcf-no-connection">
                      No Databricks connection is configured for this workspace.{' '}
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => navigate('/settings/integrations')}
                      >
                        Set one up in Settings &rarr; Integrations
                      </button>
                    </div>
                  )}

                  {/* Connection selector (if multiple) */}
                  {databricksConnections.length > 1 && (
                    <div className="form-group">
                      <label htmlFor="jcf-db-conn">Connection</label>
                      <select
                        id="jcf-db-conn"
                        className="form-input"
                        value={selectedConnection || ''}
                        onChange={(e) => {
                          setSelectedConnection(e.target.value || null);
                          setSelectedTable(null);
                        }}
                      >
                        <option value="">Select a connection...</option>
                        {databricksConnections.map(conn => (
                          <option key={conn.id} value={conn.id}>{conn.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Multi-select toggle */}
                  {selectedConnection && (
                    <label
                      className="jcf-bulk-toggle"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 10px',
                        background: 'var(--tri-card-2)',
                        border: '1px solid var(--tri-taupe)',
                        borderRadius: 6,
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={bulkSelectMode}
                        onChange={(e) => {
                          setBulkSelectMode(e.target.checked);
                          if (e.target.checked) {
                            setSelectedTable(null);
                          } else {
                            setSelectedTables([]);
                          }
                        }}
                      />
                      <span>Bulk-select multiple tables for one journey</span>
                    </label>
                  )}

                  {/* Table browser */}
                  {selectedConnection && (
                    <DatabricksTableBrowser
                      connectionId={selectedConnection}
                      onTableSelect={(table) => {
                        setSelectedTable({
                          catalog: table.catalog,
                          schema: table.schema,
                          table: table.table,
                        });
                      }}
                      onWarehouseStatus={setWarehouseState}
                      showAnalyzeButton={false}
                      multiSelect={bulkSelectMode}
                      onSelectionChange={setSelectedTables}
                    />
                  )}

                  {/* Selected table chip — single mode */}
                  {!bulkSelectMode && selectedTable && (
                    <div className="jcf-selected-table">
                      <span className="jcf-table-icon">&#9889;</span>
                      <span className="jcf-table-path">
                        {selectedTable.catalog}.{selectedTable.schema}.{selectedTable.table}
                      </span>
                    </div>
                  )}

                  {/* Selection summary — bulk mode */}
                  {bulkSelectMode && selectedTables.length > 0 && (
                    <div
                      className="jcf-bulk-summary"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '8px 12px',
                        background: 'var(--tri-teal-tint)',
                        border: '1px solid var(--tri-teal-tint-line)',
                        borderRadius: 6,
                        fontSize: '0.85rem',
                        color: 'var(--tri-teal)',
                      }}
                    >
                      <span>
                        <strong>{selectedTables.length}</strong>{' '}
                        {selectedTables.length === 1 ? 'table' : 'tables'} selected
                      </span>
                      {selectedTables.length > 20 && selectedTables.length <= 50 && (
                        <span style={{ color: 'var(--tri-soft)', fontSize: '0.8rem' }}>
                          ⚠ Large batch — runs serially and can take a while.
                        </span>
                      )}
                      {selectedTables.length > 50 && (
                        <span style={{ color: 'var(--tri-hard)', fontSize: '0.8rem' }}>
                          ⚠ Maximum 50 tables per batch — deselect{' '}
                          {selectedTables.length - 50} to continue.
                        </span>
                      )}
                    </div>
                  )}

                  {/* Databricks analysis info callout */}
                  <div className="jcf-info-callout" style={{
                    background: 'var(--tri-teal-tint)',
                    border: '1px solid var(--tri-teal-tint-line)',
                    borderRadius: '0.5rem',
                    padding: '0.75rem 1rem',
                    fontSize: '0.8rem',
                    color: 'var(--tri-teal)',
                    lineHeight: 1.5,
                    marginTop: '0.5rem',
                  }}>
                    <strong style={{ color: 'var(--tri-teal)' }}>How analysis works:</strong>{' '}
                    Aggregate statistics (counts, distributions, correlations) run as SQL on your
                    warehouse — no data leaves Databricks. A small statistical sample (~1,000 rows)
                    is transferred in-memory for advanced algorithms (near-duplicate detection, outlier
                    analysis, ML readiness). No data is persisted outside your journey.
                  </div>

                  {/* Deep analysis checkbox */}
                  {(selectedTable || (bulkSelectMode && selectedTables.length > 0)) && (
                    <label className="jcf-deep-analysis">
                      <input
                        type="checkbox"
                        checked={deepAnalysisEnabled}
                        onChange={(e) => setDeepAnalysisEnabled(e.target.checked)}
                      />
                      <div>
                        <span className="jcf-deep-label">Enable Deep ML Analysis</span>
                        <span className="jcf-deep-desc">
                          Extracts a statistical sample (~1,000-5,000 rows){bulkSelectMode && selectedTables.length > 1 ? ' per table' : ''}. Data is processed in memory and discarded after analysis.
                        </span>
                      </div>
                    </label>
                  )}

                  {/* Warehouse warnings */}
                  {(selectedTable || (bulkSelectMode && selectedTables.length > 0)) && warehouseState === 'STOPPED' && (
                    <div className="warehouse-stopped-warning">
                      <span className="warning-icon">&#9888;&#65039;</span>
                      <span>SQL Warehouse is <strong>stopped</strong>. Start it in the Databricks console before analyzing.</span>
                    </div>
                  )}
                  {(selectedTable || (bulkSelectMode && selectedTables.length > 0)) && warehouseState === 'STARTING' && (
                    <div className="warehouse-starting-info">
                      <span className="spinner-sm" />
                      <span>SQL Warehouse is starting up... The button will enable once it&apos;s ready.</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ZONE 3: Advanced Options (collapsible) */}
            <div className="jcf-zone">
              <button
                type="button"
                className="jcf-advanced-toggle"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span className={`jcf-chevron ${showAdvanced ? 'jcf-chevron--open' : ''}`}>&#9658;</span>
                <span>Advanced Options</span>
                {selectedTemplateSummary && (
                  <span className="jcf-template-badge">{selectedTemplateSummary.name}</span>
                )}
              </button>

              {showAdvanced && (
                <div className="jcf-advanced-content">
                  <div className="form-group">
                    <TemplateSelector
                      selectedTemplateId={selectedTemplateId}
                      onTemplateSelect={handleTemplateSelect}
                      placeholder="Select a use case template (optional)"
                      allowNone={true}
                    />
                    {selectedTemplateSummary && (
                      <span className="form-hint">
                        Template will configure station weights and thresholds for your assessment
                      </span>
                    )}
                  </div>

                  {/* Inline intake form */}
                  {selectedTemplate && selectedTemplate.intake_questions.length > 0 && !templateLoading && (
                    <div className="jcf-intake-inline">
                      <TemplateIntakeForm
                        template={selectedTemplate}
                        onSubmit={handleIntakeSubmit}
                        onCancel={() => { /* no-op in inline mode */ }}
                        initialAnswers={intakeAnswers}
                        isSubmitting={false}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="error-message">
                <span className="error-icon">&#9888;&#65039;</span>
                {error}
              </div>
            )}
          </div>

          {/* ===== ANALYZING PHASE ===== */}
          <div className={`jcf-analyze-phase ${phase === 'analyzing' ? 'jcf-phase--visible' : 'jcf-phase--hidden'}`}>
            <div className="analysis-progress-panel">
              {/* Header */}
              <div className="ap-header">
                <div className="ap-title">
                  <span className="ap-pulse-dot" />
                  <span>
                    {dataSource === 'databricks' && bulkTableLabel
                      ? bulkTableLabel
                      : dataSource === 'databricks' && bulkSelectMode && selectedTables.length > 0
                        ? `Analyzing ${selectedTables.length} tables...`
                        : dataSource === 'databricks' && selectedTable
                          ? `Analyzing ${selectedTable.catalog}.${selectedTable.schema}.${selectedTable.table}`
                          : assets.length > 0
                            ? `Analyzing ${assets[0].name}`
                            : 'Analyzing...'}
                  </span>
                </div>
                <div className="ap-elapsed">
                  {Math.floor(analysisElapsed / 60000)}:{String(Math.floor((analysisElapsed % 60000) / 1000)).padStart(2, '0')}
                </div>
              </div>

              {/* Progress bar */}
              <div className="ap-bar-track">
                <div className="ap-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <div className="ap-bar-label">{pct}% complete &mdash; {analysisLabel}</div>

              {/* Phase list with algorithms */}
              <div className="ap-phases">
                {analysisStages.map(stage => {
                  const algorithms = STAGE_ALGORITHMS[stage.id] || [];
                  const isActive = stage.status === 'active';
                  const isDone = stage.status === 'done';
                  const statusClass = isActive ? 'ap-phase--active' : isDone ? 'ap-phase--done' : 'ap-phase--pending';

                  return (
                    <div key={stage.id} className={`ap-phase ${statusClass}`}>
                      <div className="ap-phase-row">
                        <span className="ap-phase-icon">
                          {isDone ? '\u2713' : isActive ? '\u25CF' : '\u25CB'}
                        </span>
                        <span className="ap-phase-label">{stage.label}</span>
                        {algorithms.length > 0 && (
                          <span className="ap-phase-badge">
                            {algorithms.length} algorithm{algorithms.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      {isActive && algorithms.length > 0 && (
                        <div className="ap-algorithms">
                          {algorithms.map((algo, idx) => (
                            <div key={idx} className="ap-algo-card">
                              <span className="ap-algo-name">{algo.name}</span>
                              <span className="ap-algo-detail">{algo.detail}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {isDone && algorithms.length > 0 && (
                        <div className="ap-done-summary">
                          {algorithms.map(a => a.name).join(' \u00B7 ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Error during analysis */}
            {error && (
              <div className="error-message" style={{ marginTop: '1rem' }}>
                <span className="error-icon">&#9888;&#65039;</span>
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        {phase === 'form' && (
          <div className="modal-footer">
            <div className="footer-spacer" />
            <button
              className="btn btn-primary btn-lg"
              onClick={handleSubmit}
              disabled={!canSubmit() || templateLoading}
            >
              {templateLoading ? 'Loading template...' : isPage ? 'Import & Analyze' : 'Create & Analyze'}
            </button>
          </div>
        )}
      </div>
  );

  const problemModal = inferenceData && (
    <ProblemConfirmationModal
      isOpen={showProblemModal}
      onClose={handleProblemModalClose}
      onConfirm={handleProblemConfirm}
      journeyId={inferenceData.journeyId}
      inference={inferenceData.inference}
      suggestedDeclaration={inferenceData.suggestedDeclaration}
    />
  );

  if (isPage) {
    return (
      <>
        {formCard}
        {problemModal}
      </>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      {formCard}
      {problemModal}
    </div>
  );
};
